import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { COMPANY_STATUS_OPTIONS } from "../constants";

/** Saved views that show `created_at` (most static / status-driven views). */
const CREATED_AT_VIEWS = [
  "all-companies",
  "recent-companies",
  "status-active",
  "status-inactive",
  "status-prospect",
  "status-client",
  "status-former-client",
  "status-suspended",
  "no-tickets-this-month",
  "with-tickets-this-month",
  "no-tickets-last-month",
  "with-tickets-last-month",
] as const;

/** Saved views that show `updated_at` (all-companies, client deal views, ticket-derived views). */
const UPDATED_AT_VIEWS = [
  "all-companies",
  "clients-with-active-deals",
  "clients-without-active-deals",
  "no-tickets-this-month",
  "with-tickets-this-month",
  "no-tickets-last-month",
  "with-tickets-last-month",
] as const;

export const companyMetadataFields = defineFieldConfigMap({
    // FIELD: description
    description: {
      type: "text",
      label: "Description",
      mobileDetailSectionId: "basic",
      sortable: false,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      formSpan: 3,
      filterType: "contains",
      width: 280,
      minWidth: 200,
      maxWidth: 400,
      placeholder: "Business description for the team",
      icon: "FileText",
      allowedRoles: ["client_user", "wtc_employee", "wtc_admin"],
      graphqlSelector: "description",
    },
    // FIELD: notes
    notes: {
      type: "text",
      label: "Notes",
      mobileDetailSectionId: "basic",
      sortable: false,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      formSpan: 3,
      filterType: "contains",
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      placeholder: "Internal notes",
      icon: "StickyNote",
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "notes",
    },
    // FIELD: quickbooks_customer_name
    quickbooks_customer_name: {
      type: "text",
      label: "QB Customer Name",
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false, // Read-only from QuickBooks
      filterType: "contains",
      width: 180,
      minWidth: 150,
      maxWidth: 250,
      placeholder: "QuickBooks name",
      icon: "Calculator",
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "quickbooks_customer_name",
    },
    // FIELD: quickbooks_sync_at
    quickbooks_sync_at: {
      type: "datetime",
      label: "QB Last Sync",
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: false, // Datetime fields typically not searchable
      resizable: true,
      hideable: true,
      editable: false, // Auto-generated
      width: 140,
      minWidth: 120,
      maxWidth: 180,
      icon: "RefreshCw",
      groupable: true,
      groupingOptions: {
        dateGranularity: "day",
      },
      datetimePresets: {
        enabled: true,
        presets: ["today", "yesterday", "last7days", "last30days", "thismonth", "lastmonth"],
        showCustomRange: true,
      },
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "quickbooks_sync_at",
    },
    // FIELD: google_drive_folder_id — friendly "📁 Open folder" link (see
    // field-display-renderer). Inline-editing opens the Drive folder picker
    // modal (input-factory → "drive-folder" → DriveFolderInput).
    google_drive_folder_id: {
      type: "text",
      label: "Drive folder",
      mobileDetailSectionId: "metadata",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      icon: "FolderOpen",
      graphqlSelector: "google_drive_folder_id",
    },
    // FIELD: created_at
    created_at: {
      type: "datetime",
      label: "Created",
      // DB-managed (default now()). Display-only — never sent on write.
      excludeFromApi: true,
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: false, // Datetime fields typically not searchable
      resizable: true,
      hideable: true,
      editable: false, // Auto-generated timestamp
      width: 160,
      minWidth: 120,
      maxWidth: 220,
      icon: "Calendar",
      groupable: true,
      groupingOptions: {
        dateGranularity: "month",
      },
      datetimePresets: {
        enabled: true,
        presets: [
          "today",
          "yesterday",
          "last7days",
          "last30days",
          "thismonth",
          "lastmonth",
          "thisyear",
          "lastyear",
        ],
        showCustomRange: true,
      },
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "created_at",
      savedViewMembership: CREATED_AT_VIEWS,
    },
    // FIELD: updated_at
    updated_at: {
      type: "datetime",
      label: "Updated",
      // DB-managed (default now()). Display-only — never sent on write.
      excludeFromApi: true,
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: false, // Datetime fields typically not searchable
      resizable: true,
      hideable: true,
      editable: false, // Auto-generated timestamp
      width: 160,
      minWidth: 120,
      maxWidth: 220,
      icon: "Calendar",
      groupable: true,
      groupingOptions: {
        dateGranularity: "month",
      },
      datetimePresets: {
        enabled: true,
        presets: [
          "today",
          "yesterday",
          "last7days",
          "last30days",
          "thismonth",
          "lastmonth",
          "thisyear",
          "lastyear",
        ],
        showCustomRange: true,
      },
      // Internal/financial — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "updated_at",
      savedViewMembership: UPDATED_AT_VIEWS,
    },
    // FIELD: created_by — who created this company. Filter uses the user picker
    // (first option resolves to the current logged-in user). Stamped on insert
    // by the companies_audit_trigger; NULL for pre-existing / service-role rows.
    created_by: {
      type: "text",
      label: "Created By",
      isForeignKey: true,
      foreignKey: {
        referencedTable: "app_users",
        referencedColumn: "id",
        constraintName: "companies_created_by_fkey",
        onDelete: "SET NULL",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "user-select",
      width: 140,
      minWidth: 120,
      maxWidth: 180,
      placeholder: "User ID",
      icon: "User",
      // DB-trigger-managed (companies_audit_trigger). Display + filter only —
      // never sent on write.
      excludeFromApi: true,
      // Internal — staff only.
      allowedRoles: ["wtc_employee", "wtc_admin"],
      graphqlSelector: "created_by",
    },
});
